import { getGroq, GROQ_MODELS } from './groq-client';

export interface CaseSummaryParams {
  caseTitle: string;
  caseDescription: string | null;
  caseNumber: string;
  practiceArea: string;
  country: string;
  status: string;
  priority: string;
  documents: Array<{
    title: string;
    document_type: string;
    created_at: string;
  }>;
  events: Array<{
    event_title: string;
    event_description: string | null;
    event_date: string;
    event_type: string;
  }>;
  notes?: Array<{
    content: string;
    author_name: string;
    timestamp: string;
  }>;
  lawyerName?: string;
  startedAt?: string | null;
}

export interface CaseSummaryResult {
  summary: string;
  keyPoints: string[];
  nextSteps: string[];
  timeline: string;
  recommendations: string[];
}

/**
 * Generate AI-powered case summary
 */
export async function generateCaseSummary(
  params: CaseSummaryParams
): Promise<CaseSummaryResult> {
  const groq = getGroq();

  const {
    caseTitle,
    caseDescription,
    caseNumber,
    practiceArea,
    country,
    status,
    priority,
    documents,
    events,
    notes,
    lawyerName,
    startedAt,
  } = params;

  const countryContext =
    {
      MX: 'México',
      RD: 'República Dominicana',
      CO: 'Colombia',
      CL: 'Chile',
      PE: 'Perú',
    }[country] || 'Latinoamérica';

  const systemPrompt = `Eres un asistente legal especializado en ${practiceArea} bajo el marco jurídico de ${countryContext}.
Tu tarea es generar un resumen ejecutivo estructurado del caso legal basándote ÚNICAMENTE en la información proporcionada.

REGLAS ABSOLUTAS — incumplirlas invalida el resumen:
1. SOLO analiza y resume la información que el usuario proporcionó explícitamente. NO inventes hechos, fechas, montos, partes, leyes, artículos ni precedentes que no estén en el input.
2. Si la información es insuficiente para una sección, indícalo con "Información no proporcionada" en lugar de inventar contenido.
3. Nunca cites artículos, leyes o jurisprudencia específica a menos que el usuario los haya mencionado explícitamente.
4. No generes nombres propios, empresas ni montos que no estén en el texto del usuario.
5. Las recomendaciones deben ser generales y metodológicas (ej: "verificar la documentación", "consultar con el cliente"), nunca estrategias legales específicas basadas en hechos inventados.
6. Tu función es ORGANIZAR y RESUMIR la información existente, no asesorar legalmente ni inventar información nueva.

El resumen debe incluir:
1. Un resumen general del caso (2-3 párrafos) — solo con la info proporcionada
2. Puntos clave identificados (lista de 5-7 puntos)
3. Próximos pasos metodológicos (lista de 3-5 acciones generales)
4. Timeline de los eventos proporcionados
5. Recomendaciones procedimentales (lista de 3-5 sugerencias)

Usa terminología legal apropiada para ${countryContext} y responde siempre en español.`;

  const documentsText = documents
    .map(
      (doc, idx) =>
        `${idx + 1}. ${doc.title} (${doc.document_type}) - ${new Date(doc.created_at).toLocaleDateString()}`
    )
    .join('\n');

  const eventsText = events
    .map(
      (event, idx) =>
        `${idx + 1}. ${event.event_date}: ${event.event_title}${event.event_description ? ` - ${event.event_description}` : ''}`
    )
    .join('\n');

  const notesText =
    notes && notes.length > 0
      ? notes
          .map(
            (note, idx) =>
              `${idx + 1}. [${note.author_name}] ${note.content} (${new Date(note.timestamp).toLocaleDateString()})`
          )
          .join('\n')
      : 'No hay notas registradas.';

  const userPrompt = `Genera un resumen ejecutivo del siguiente caso legal:

INFORMACIÓN DEL CASO:
- Título: ${caseTitle}
- Número de caso: ${caseNumber}
- Área de práctica: ${practiceArea}
- País: ${countryContext}
- Estado: ${status}
- Prioridad: ${priority}
${caseDescription ? `- Descripción: ${caseDescription}` : ''}
${lawyerName ? `- Abogado asignado: ${lawyerName}` : ''}
${startedAt ? `- Fecha de inicio: ${new Date(startedAt).toLocaleDateString()}` : ''}

DOCUMENTOS ASOCIADOS (${documents.length}):
${documents.length > 0 ? documentsText : 'No hay documentos asociados.'}

EVENTOS DEL CASO (${events.length}):
${events.length > 0 ? eventsText : 'No hay eventos registrados.'}

NOTAS:
${notesText}

Genera el resumen en formato JSON con la siguiente estructura:
{
  "summary": "Resumen general del caso (2-3 párrafos)",
  "keyPoints": ["Punto clave 1", "Punto clave 2", ...],
  "nextSteps": ["Paso 1", "Paso 2", ...],
  "timeline": "Timeline resumido en texto",
  "recommendations": ["Recomendación 1", "Recomendación 2", ...]
}`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS, // Use Mixtral for best quality
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 3000,
      response_format: { type: 'json_object' },
    });

    const content = completion.choices[0]?.message?.content;

    if (!content) {
      throw new Error('No content generated by Groq');
    }

    // Parse JSON response
    const result = JSON.parse(content);

    // Validate and return
    return {
      summary: result.summary || 'No se pudo generar el resumen.',
      keyPoints: Array.isArray(result.keyPoints)
        ? result.keyPoints
        : ['No hay puntos clave disponibles.'],
      nextSteps: Array.isArray(result.nextSteps)
        ? result.nextSteps
        : ['No hay próximos pasos definidos.'],
      timeline: result.timeline || 'No hay timeline disponible.',
      recommendations: Array.isArray(result.recommendations)
        ? result.recommendations
        : ['No hay recomendaciones disponibles.'],
    };
  } catch (error: any) {
    console.error('Error generating case summary:', error);

    // Fallback summary
    return {
      summary: `Caso ${caseNumber}: ${caseTitle}. Estado actual: ${status}. Prioridad: ${priority}.`,
      keyPoints: [
        `Área de práctica: ${practiceArea}`,
        `Estado: ${status}`,
        `Prioridad: ${priority}`,
        `Documentos: ${documents.length}`,
        `Eventos: ${events.length}`,
      ],
      nextSteps: [
        'Revisar documentos del caso',
        'Coordinar con el abogado asignado',
        'Actualizar estado según avances',
      ],
      timeline: `Caso iniciado${startedAt ? ` el ${new Date(startedAt).toLocaleDateString()}` : ''}. ${events.length} eventos registrados.`,
      recommendations: [
        'Mantener comunicación regular con el abogado',
        'Documentar todos los eventos importantes',
        'Revisar documentos periódicamente',
      ],
    };
  }
}

